import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import axiosInstance from 'api/axiosInstance';
import apiUrls from 'api/apiUrls';
import Cookies from 'js-cookie';
import { sortDevices } from 'views/hooks/sortDevices';
import { useToast } from '@chakra-ui/react';

const DeviceContext = createContext();

export const DeviceProvider = ({ children }) => {
  const userAuthority = localStorage.getItem('userAuthority');
  const [deviceData, setDeviceData] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDevices, setTotalDevices] = useState(0);
  const latestSearchRef = useRef('');
  const customerId = localStorage.getItem('customerId');
  const [totalActiveDevices, setTotalActiveDevices] = useState(0);
  const [totalInactiveDevices, setTotalInactiveDevices] = useState(0);
  const toast = useToast();

  const fetchAllDevicesAllPages = useCallback(async (serialNumber = '') => {
    let page = 0;
    let allDevices = [];
    let hasNextPage = true;

    while (hasNextPage) {
      const apiUrl = apiUrls.allDevices(serialNumber, page);
      try {
        const resp = await axiosInstance.get(apiUrl);
        const devices = resp.data.data;
        allDevices = allDevices.concat(devices);
        hasNextPage = resp.data.hasNext;
        page += 1;
      } catch (error) {
        console.error('Error fetching devices:', error);
        break;
      }
    }

    // Calculate total active devices
    const activeCount = allDevices.filter((d) => d.active).length;
    const inactiveCount = allDevices.length - activeCount;
    console.log('Total active devices:', activeCount);

    setTotalActiveDevices(activeCount);
    setTotalInactiveDevices(inactiveCount);

    // Optionally, you can process allDevices further or setDeviceData here if needed
  }, []);

  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      fetchAllDevicesAllPages('');
    }
  }, [fetchAllDevicesAllPages]);

  const fetchDeviceAttributes = useCallback(async (deviceName) => {
    const keys =
      'warranty_date,location,app_version,device_type,patch_v,group_id,history,modbus_config_name,patch_name,config_status,patch_status,modbus_v';

    try {
      const resp = await axiosInstance.get(
        apiUrls.factorytoolgroupAttributes(deviceName, keys),
      );

      const attrs = resp.data;
      const attrMap = Object.fromEntries(attrs.map((a) => [a.key, a.value]));

      if (attrMap.group_id) {
        localStorage.setItem('groupID', attrMap.group_id);
      }

      return {
        warranty: attrMap.warranty_date ?? null,
        location: attrMap.location ?? '',
        app_version: attrMap.app_version ?? '-',
        device_type: attrMap.device_type ?? 0,
        patch_v: attrMap.patch_v ?? '-',
        group_id: attrMap.group_id ?? null,
        history: attrMap.history ?? [],
        modbus_config_name: attrMap.modbus_config_name ?? '-',
        patch_name: attrMap.patch_name ?? '-',
        config_status: attrMap.config_status ?? '-',
        patch_status: attrMap.patch_status ?? '-',
      };
    } catch (error) {
      if (error?.response?.status === 404) {
        return null;
      }

      console.error('Error fetching attributes:', error);
      return null;
    }
  }, []);

  const fetchDeviceAssetId = async (deviceId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getRelationInfo(deviceId));

      const assetRelation = resp.data.find(
        (r) => r.from?.entityType === 'ASSET',
      );

      return assetRelation?.from?.id || null;
    } catch (error) {
      console.error('Error fetching device asset relation:', error);
      return null;
    }
  };

  const fetchAssetAdditionalInfo = async (assetId) => {
    try {
      const resp = await axiosInstance.get(apiUrls.getAdditionalInfo(assetId));
      return resp.data?.additionalInfo || {};
    } catch (error) {
      console.error('Error fetching asset info:', error);
      return {};
    }
  };

  const fetchAllDevices = useCallback(
    async (serialNumber = '', page = 0) => {
      latestSearchRef.current = serialNumber;

      const pageNumber = typeof page === 'number' && page >= 0 ? page : 0;

      setLoadingDevices(true);

      try {
        let resp;

        try {
          const apiUrl = apiUrls.allDevices(serialNumber, pageNumber);
          resp = await axiosInstance.get(apiUrl);
        } catch (apiError) {
          setLoadingDevices(false);
          return;
        }

        const devices = resp.data.data;

        const sortedNames = sortDevices(devices.map((d) => d.name));

        const sortedDevices = sortedNames.map((name) =>
          devices.find((d) => d.name === name),
        );
        setHasNext(resp.data.hasNext);
        setTotalPages(resp.data.totalPages ?? 0);
        setTotalDevices(resp.data.totalElements ?? devices.length);

        const temp = await Promise.all(
          sortedDevices.map(async (d) => {
            const attrs = await fetchDeviceAttributes(d.name);
            if (!attrs) return null;

            // Use the active field from the device response
            const active = d.active ?? false;

            const assetId = await fetchDeviceAssetId(d?.id?.id);

            let assetInfo = {};
            if (assetId) {
              assetInfo = await fetchAssetAdditionalInfo(assetId);
            }

            return {
              deviceId: d?.id?.id,
              machineModel: d.label || '',
              serialNumber: d.name,
              name: d.name,
              active: active, // Using active from device response
              ...attrs,
              country: assetInfo?.CountryName || '-',
              state: assetInfo?.StateName || '-',
              noc: assetInfo?.NocName || '-',
              plant: assetInfo?.DisplayName || '-',
              history: attrs.history ?? [],
              modbus_config_name: attrs.modbus_config_name ?? '-',
              patch_name: attrs.patch_name ?? '-',
            };
          }),
        );

        if (latestSearchRef.current === serialNumber) {
          setDeviceData(temp.filter(Boolean));
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
      } finally {
        setLoadingDevices(false);
      }
    },
    [fetchDeviceAttributes],
  );

  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      fetchAllDevices('', 0);
    }
  }, [fetchAllDevices]);

  return (
    <DeviceContext.Provider
      value={{
        deviceData,
        loadingDevices,
        hasNext,
        totalPages,
        totalDevices,
        fetchAllDevices,
        fetchDeviceAttributes,
        fetchDeviceAssetId,
        fetchAssetAdditionalInfo,
        setDeviceData,
        totalActiveDevices,
        fetchAllDevices,
        totalInactiveDevices,
      }}
    >
      {children}
    </DeviceContext.Provider>
  );
};

export const useDevices = () => useContext(DeviceContext);
